import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import {
  AboutWrapper,
  AboutContent,
  ProfileSection,
  ProfileImage,
  AboutTitle,
  AboutDescription,
  AboutText,
  Stats,
  StatItem,
  StatNumber,
  StatLabel
} from './styles';

const FAQSection = styled.div`
  margin-top: 4rem;
`;

const FAQTitle = styled.h2`
  color: #2F4F4F;
  font-size: 2rem;
  text-align: center;
  margin-bottom: 3rem;
`;

const FAQItem = styled.div`
  margin-bottom: 1rem;
  border: 1px solid rgba(47, 79, 79, 0.1);
  border-radius: 8px;
  overflow: hidden;
`;

const FAQQuestion = styled.button`
  width: 100%;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  color: #2F4F4F;
  text-align: right;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(47, 79, 79, 0.05);
  }
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 1.5rem;
  color: #666;
  line-height: 1.6;
  font-size: 1.1rem;
`;

const AnswerContent = styled.div`
  padding: 1.5rem 0;
  border-top: 1px solid rgba(47, 79, 79, 0.1);
`;

const FAQText = styled.p`
  color: #666;
  line-height: 1.8;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const About = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  const faqData = [
    {
      question: "למה כדאי לקחת יועץ משכנתאות חיצוני?",
      answer: `יועץ משכנתאות חיצוני פועל לטובת האינטרסים שלכם בלבד, ולא של הבנק. היתרונות המרכזיים:
      • השוואת תנאים בין כל הבנקים ומציאת העסקה הטובה ביותר
      • חיסכון משמעותי בעלויות המשכנתא לאורך השנים
      • ליווי אישי ומקצועי לאורך כל התהליך
      • ניסיון עשיר בניהול משא ומתן מול הבנקים
      • התאמה מדויקת של המשכנתא לצרכים והיכולות שלכם`
    },
    {
      question: "מה ההבדל בין יועץ חיצוני ליועץ בנק?",
      answer: `יועץ בנק מוגבל למוצרים של הבנק שלו ופועל לטובת האינטרסים של הבנק. לעומת זאת, יועץ חיצוני:
      • בוחן את כל האפשרויות בשוק
      • אינו מחויב לבנק ספציפי
      • מציע פתרונות מותאמים אישית
      • מנהל משא ומתן מול כל הבנקים
      • מלווה אתכם גם אחרי חתימת המשכנתא`
    },
    {
      question: "האם יועץ משכנתאות חיצוני באמת יכול לחסוך לי כסף?",
      answer: `בהחלט! החיסכון מגיע ממספר מקורות:
      • השגת ריביות נמוכות יותר
      • בחירת מסלולים אופטימליים
      • חיסכון בעמלות ועלויות נלוות
      • תכנון נכון של המשכנתא לטווח ארוך
      • מניעת טעויות יקרות בתהליך`
    },
    {
      question: "מתי הזמן הנכון לפנות ליועץ משכנתאות?",
      answer: `מומלץ לפנות ליועץ משכנתאות כבר בשלבים הראשונים של חיפוש הנכס. זה מאפשר:
      • הבנה מדויקת של התקציב האמיתי שלכם
      • תכנון פיננסי נכון
      • הכנה מוקדמת של כל המסמכים הנדרשים
      • זמן מספיק להשוואת הצעות
      • ניהול משא ומתן אפקטיבי`
    }
  ];

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <AboutWrapper>
      <motion.div {...fadeIn}>
        <AboutTitle>אודות</AboutTitle>
      </motion.div>

      <AboutContent>
        <ProfileSection
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <ProfileImage 
            src="/images/profile/afik-profile.jpg" 
            alt="אפיק לב - יועצת משכנתאות" 
          />
        </ProfileSection>

        <AboutDescription
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <AboutText>
            שלום, אני אפיק לב, יועצת משכנתאות מוסמכת עם ניסיון של למעלה מ-5 שנים בתחום המשכנתאות והייעוץ הפיננסי.
          </AboutText>
          
          <AboutText>
            כיועצת משכנתאות עצמאית, אני מתמחה בליווי אישי ומקצועי של לקוחותיי בתהליך לקיחת המשכנתא, תוך התאמת הפתרון המיטבי לצרכים והיכולות הכלכליות של כל לקוח.
          </AboutText>

          <Stats>
            <StatItem>
              <StatNumber>500+</StatNumber>
              <StatLabel>לקוחות מרוצים</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>5+</StatNumber>
              <StatLabel>שנות ניסיון</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>98%</StatNumber>
              <StatLabel>שביעות רצון</StatLabel>
            </StatItem>
          </Stats>
        </AboutDescription>
      </AboutContent>

      <FAQSection>
        <FAQTitle>שאלות נפוצות</FAQTitle>
        {faqData.map((faq, index) => (
          <FAQItem key={index}>
            <FAQQuestion onClick={() => setOpenFAQ(openFAQ === index ? null : index)}>
              {faq.question}
              {openFAQ === index ? <ExpandLess /> : <ExpandMore />}
            </FAQQuestion>
            <AnimatePresence>
              {openFAQ === index && (
                <FAQAnswer
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AnswerContent>
                    {faq.answer.split('\n').map((line, i) => (
                      <FAQText key={i}>{line}</FAQText>
                    ))}
                  </AnswerContent>
                </FAQAnswer>
              )}
            </AnimatePresence>
          </FAQItem>
        ))}
      </FAQSection>
    </AboutWrapper>
  );
};

export default About; 